import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { impersonationAPI, ImpersonationWhoami } from '@hpanel/web-common/api/impersonation';

interface ImpersonationContextValue {
  isImpersonating: boolean;
  event: ImpersonationWhoami | null;
  loading: boolean;
  end: () => Promise<void>;
}

const Ctx = createContext<ImpersonationContextValue | undefined>(undefined);

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [event, setEvent] = useState<ImpersonationWhoami | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const w = await impersonationAPI.whoami();
        if (!cancelled) setEvent(w);
      } catch (e) {
        // §9 — never surface raw error; log for dev console.
        // eslint-disable-next-line no-console
        console.error('[ImpersonationContext] whoami failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Task 29: best-effort sendBeacon to /end on tab close.
  useEffect(() => {
    function onUnload() {
      try {
        navigator.sendBeacon(
          '/api/auth/impersonation/end',
          new Blob([], { type: 'application/json' }),
        );
      } catch {
        // best-effort; the 30-min TTL is the backstop.
      }
    }
    if (event !== null) {
      window.addEventListener('beforeunload', onUnload);
      return () => window.removeEventListener('beforeunload', onUnload);
    }
  }, [event]);

  const end = useCallback(async () => {
    await impersonationAPI.end();
    setEvent(null);
  }, []);

  return (
    <Ctx.Provider value={{ isImpersonating: event !== null, event, loading, end }}>
      {children}
    </Ctx.Provider>
  );
};

export function useImpersonation(): ImpersonationContextValue {
  const v = useContext(Ctx);
  if (v === undefined) {
    throw new Error('useImpersonation must be used inside <ImpersonationProvider>');
  }
  return v;
}
